import React, { useEffect, useRef, useState } from "react";
import { Loader2, Sparkles, ShoppingCart, Check, Gift, Plus } from "lucide-react";
import http from "@/api";
import { useApp } from "@/context";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";
const imgOf = (u) => (u && u.startsWith("/") ? `${API_BASE}${u}` : u);
const track = (payload) => http.post("/cross-sell/track", payload).catch(() => {});

export default function CompleteGift({ product, lang }) {
  const { addToCart } = useApp();
  const ar = lang === "ar";
  const [items, setItems] = useState(null);
  const [sel, setSel] = useState(null);
  const [img, setImg] = useState("");
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [added, setAdded] = useState(false);
  const [addedIds, setAddedIds] = useState({});
  const [err, setErr] = useState("");
  const rootRef = useRef(null);
  const seenRef = useRef(false);

  // جلب كسول — لا يؤخر تحميل صفحة المنتج إطلاقاً
  useEffect(() => {
    if (!product?.id) return;
    setItems(null); setSel(null); setImg(""); setErr(""); setAddedIds({});
    seenRef.current = false;
    const tm = setTimeout(() => {
      http.get(`/products/${product.id}/cross-sell`, { timeout: 20000 })
        .then(({ data }) => setItems(data.suggestions || []))
        .catch(() => setItems([]));
    }, 60);
    return () => clearTimeout(tm);
  }, [product?.id]);

  // تتبع المشاهدة — مرة واحدة عند ظهور القسم فعلياً على الشاشة
  useEffect(() => {
    if (!items || items.length === 0 || !rootRef.current || seenRef.current) return;
    const ob = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting) && !seenRef.current) {
        seenRef.current = true;
        track({ product_id: product.id, event: "impression", companion_ids: items.map((x) => x.id) });
        ob.disconnect();
      }
    }, { threshold: 0.3 });
    ob.observe(rootRef.current);
    return () => ob.disconnect();
  }, [items, product?.id]);

  if (!items || items.length === 0) return null;

  const combine = async (c) => {
    if (loading) return;
    setSel(c); setImg(""); setErr(""); setAdded(false); setLoading(true);
    track({ product_id: product.id, event: "click_combine", companion_id: c.id });
    try {
      const { data } = await http.post("/ai/product-combine",
        { product_id: product.id, companion_id: c.id, language: lang }, { timeout: 120000 });
      setImg(data.image);
    } catch {
      setErr(ar ? "تعذّر الدمج — حاول مجدداً" : "Couldn't combine — try again");
      setSel(null);
    }
    setLoading(false);
  };

  const quickAdd = (c) => {
    addToCart(c, 1);
    setAddedIds((m) => ({ ...m, [c.id]: true }));
    setTimeout(() => setAddedIds((m) => {
      const n = { ...m }; delete n[c.id]; return n;
    }), 2500);
    track({ product_id: product.id, event: "add", companion_id: c.id });
  };

  const orderBoth = async () => {
    if (!img || !sel || adding) return;
    setAdding(true); setErr("");
    try {
      const { data } = await http.post("/ai/preview-save", { image: img }, { timeout: 60000 });
      const label = (ar ? "مع " : "With ") + (ar ? sel.name_ar : sel.name_en || sel.name_ar);
      addToCart(product, 1, {
        id: `ai-custom-combo-${Date.now() % 100000}`,
        name: label,
        price_sar: Number(product.price_sar) || 0,
        image: data.url,
        custom_color: { label, image: data.url, addon: "combo" },
      });
      addToCart(sel, 1);
      setAdded(true);
      track({ product_id: product.id, event: "order_both", companion_id: sel.id });
      setTimeout(() => setAdded(false), 6000);
    } catch {
      setErr(ar ? "تعذّر الحفظ — حاول مجدداً" : "Couldn't save — try again");
    }
    setAdding(false);
  };

  const total = (Number(product?.price_sar) || 0) + (Number(sel?.price_sar) || 0);
  const badgeOf = (c) => {
    if (c.reason === "bought_together") return ar ? "🔥 يُطلب معاً" : "🔥 Popular pair";
    if (c.reason === "manual") return ar ? "🎀 اختيارنا" : "🎀 Our pick";
    return "";
  };

  return (
    <div ref={rootRef} className="rounded-2xl border overflow-hidden" style={{ borderColor: "var(--border)" }} data-testid="complete-gift">
      <div className="px-4 pt-3">
        <div className="text-sm font-black flex items-center gap-2">
          <Gift size={17} className="text-emerald-700" /> {ar ? "أكمل هديتك 🎁" : "Complete your gift 🎁"}
        </div>
        <div className="text-[11px] opacity-60 mt-0.5">
          {ar ? "منتجات تكمّل هديتك — أضفها مباشرة بلمسة، أو شاهدها مع هديتك بصورة واحدة"
              : "Picks that complete your gift — add instantly, or see them together in one photo"}
        </div>
      </div>
      <div className="px-4 py-3 space-y-3">
        <div className="flex gap-2 overflow-x-auto pb-1 snap-x" data-testid="complete-gift-list">
          {items.map((c) => {
            const on = sel?.id === c.id;
            const justAdded = !!addedIds[c.id];
            const badge = badgeOf(c);
            return (
              <div key={c.id}
                   className={`shrink-0 w-[122px] rounded-xl border-2 p-1.5 text-center snap-start transition-all ${on ? "border-emerald-700 bg-emerald-50" : "border-[var(--border)]"}`}
                   data-testid={`complete-gift-item-${c.id}`}>
                <div className="relative">
                  <img src={imgOf(c.image)} alt="" className="w-full h-20 rounded-lg object-cover" loading="lazy" />
                  {badge && (
                    <span className="absolute top-1 start-1 text-[8px] font-black px-1 py-0.5 rounded bg-white/90 text-emerald-800 shadow-sm">
                      {badge}
                    </span>
                  )}
                </div>
                <div className="text-[9.5px] font-bold truncate mt-1">{ar ? c.name_ar : c.name_en || c.name_ar}</div>
                <div className="text-[10px] font-black tabular-nums text-emerald-800">
                  +{Number(c.price_sar).toLocaleString("en")} {ar ? "ر.ق" : "QAR"}
                </div>
                <div className="flex gap-1 mt-1">
                  <button onClick={() => quickAdd(c)}
                          className={`flex-1 rounded-lg py-1 text-[9.5px] font-black flex items-center justify-center gap-0.5 transition-colors ${justAdded ? "bg-emerald-600 text-white" : "bg-emerald-700 text-white active:scale-95"}`}
                          data-testid={`complete-gift-add-${c.id}`}>
                    {justAdded ? <Check size={10} /> : <Plus size={10} />}
                    {justAdded ? (ar ? "أُضيف ✓" : "Added ✓") : (ar ? "أضف" : "Add")}
                  </button>
                  <button onClick={() => combine(c)} disabled={loading}
                          aria-pressed={on}
                          className={`flex-1 rounded-lg py-1 text-[9.5px] font-bold flex items-center justify-center gap-0.5 border transition-colors ${on ? "border-emerald-700 text-emerald-800 bg-emerald-100" : "border-[var(--border)] opacity-80"}`}
                          data-testid={`complete-gift-combine-${c.id}`}>
                    <Sparkles size={10} /> {ar ? "معاً" : "Pair"}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
        {loading && (
          <div className="flex items-center gap-2 text-[11px] font-semibold text-emerald-900 py-1" data-testid="complete-gift-loading">
            <Loader2 size={14} className="animate-spin" /> {ar ? "نجمعهما لك في صورة واحدة... ١٠-٢٠ ثانية" : "Combining them in one photo... 10-20s"}
          </div>
        )}
        {img && !loading && (
          <>
            <img src={img} alt="" className="w-full rounded-xl" data-testid="complete-gift-image" />
            <button onClick={orderBoth} disabled={adding}
                    className="btn-primary w-full !py-2.5 text-sm" data-testid="complete-gift-order">
              {adding ? <Loader2 size={15} className="animate-spin" /> : added ? <Check size={15} /> : <ShoppingCart size={15} />}
              {added ? (ar ? "أُضيفا للسلة ✓" : "Added ✓")
                     : (ar ? `اطلبهما معاً 🎁 — ${total.toLocaleString("en")} ر.ق` : `Order both 🎁 — ${total.toLocaleString("en")} QAR`)}
            </button>
          </>
        )}
        {err && <div className="text-xs text-red-600" data-testid="complete-gift-error">{err}</div>}
      </div>
    </div>
  );
}
